/**
 * Local calendar dates as YYYY-MM-DD.
 *
 * `toISOString().split('T')[0]` gives the *UTC* date, which is the previous day
 * for the first couple of hours after local midnight in timezones ahead of UTC
 * (SAST is UTC+2), so anything deriving "today" that way shows yesterday until
 * 02:00. Use these helpers wherever a date string stands for a day on the
 * user's calendar rather than an instant in time.
 *
 * These are deliberately NOT for timestamps. A real instant (when a meal was
 * eaten, when an error was reported) still belongs in UTC via toISOString().
 */

/** Format a Date as its local calendar date, e.g. 2026-08-04. */
export function toLocalDateStr(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/** Today's local calendar date. */
export function todayLocalDateStr(): string {
    return toLocalDateStr(new Date());
}

/**
 * Parse a YYYY-MM-DD string to local midnight. `new Date('2026-08-04')` parses
 * as UTC midnight, which is the *previous* local day west of UTC and 02:00 on
 * the day itself in SAST, so date arithmetic on it drifts.
 */
export function fromLocalDateStr(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/** Whether a YYYY-MM-DD string is today's local calendar date. */
export function isTodayLocalDateStr(value: string): boolean {
    return value === todayLocalDateStr();
}

/** A short day for labels, e.g. "Fri 9 Oct". */
export function formatShortDay(value: string): string {
    return fromLocalDateStr(value).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
}
